/**
 * Agenda unifié anti-conflit (/app/agenda) — Option A : Google Calendar.
 * Fusionne le Google Agenda de Ralph + les réservations privées KAIZEN,
 * et met en évidence les conflits (double-réservations).
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  CalendarDays, AlertTriangle, Loader2, RefreshCw, Link as LinkIcon,
  CheckCircle2, CircleAlert, ExternalLink, Upload,
} from "lucide-react";
import { PageHeader } from "../components/ui/kaizen";

const DAYS_FR = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MONTHS_FR = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

function fmtDate(iso) {
  const d = new Date(iso);
  return `${DAYS_FR[d.getDay()]} ${d.getDate()} ${MONTHS_FR[d.getMonth()]}`;
}
function fmtTime(iso, allDay) {
  if (allDay) return "Journée";
  const d = new Date(iso);
  return d.toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" });
}

export default function AgendaModule() {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(14);

  const loadStatus = useCallback(async () => {
    try { const r = await api.get("/agenda/status"); setStatus(r.data); } catch (e) { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/agenda/unified?days=${days}`);
      setData(r.data);
    } catch (e) {
      toast.error("Erreur chargement agenda");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { load(); }, [load]);

  const connectCalendar = async () => {
    try {
      const r = await api.get("/oauth/gmail/login");
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error("Impossible de démarrer la connexion Google");
    }
  };

  const pushToGoogle = async (ev) => {
    try {
      const r = await api.post(`/agenda/push-booking/${ev.id}`);
      toast.success("Ajouté à Google Agenda");
      if (r.data.html_link) window.open(r.data.html_link, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'ajout");
    }
  };

  const [showReminder, setShowReminder] = useState(false);
  const [rem, setRem] = useState({ title: "", date: "", time: "09:00", description: "" });
  const [savingRem, setSavingRem] = useState(false);

  const createReminder = async () => {
    if (!rem.title.trim() || !rem.date) { toast.error("Titre et date requis"); return; }
    setSavingRem(true);
    try {
      const r = await api.post("/agenda/reminder", { ...rem, duration_minutes: 30 });
      toast.success("Rappel ajouté à Google Agenda");
      if (r.data.html_link) window.open(r.data.html_link, "_blank");
      setShowReminder(false);
      setRem({ title: "", date: "", time: "09:00", description: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la création du rappel");
    } finally { setSavingRem(false); }
  };

  // group events by date
  const grouped = {};
  (data?.events || []).forEach((ev) => {
    const key = (ev.start || "").slice(0, 10);
    (grouped[key] = grouped[key] || []).push(ev);
  });
  const dateKeys = Object.keys(grouped).sort();

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto" data-testid="agenda-module">
      <PageHeader
        title="Agenda unifié"
        icon={CalendarDays}
        breadcrumbs={[{ label: "Compétition & événements" }, { label: "Agenda" }]}
        subtitle="Google Agenda + réservations privées, sans double-réservation."
        actions={
          <>
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white">
              <option value={7}>7 jours</option>
              <option value={14}>14 jours</option>
              <option value={30}>30 jours</option>
            </select>
            {status?.calendar_access && (
              <button onClick={() => setShowReminder((v) => !v)} className="px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm flex items-center gap-2" data-testid="add-reminder-btn">
                <CalendarDays size={16} /> Ajouter un rappel
              </button>
            )}
            <button onClick={load} className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm flex items-center gap-2" data-testid="agenda-refresh-btn">
              <RefreshCw size={16} /> Actualiser
            </button>
          </>
        }
      />

      {/* Formulaire — Ajouter un rappel (Google Agenda) */}
      {showReminder && status?.calendar_access && (
        <div className="bg-zinc-900 border border-amber-800/40 rounded-xl p-4 mb-5" data-testid="reminder-form">
          <div className="font-semibold text-amber-200 mb-3 flex items-center gap-2"><CalendarDays size={16} /> Nouveau rappel</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input value={rem.title} onChange={(e) => setRem({ ...rem, title: e.target.value })} placeholder="Titre du rappel *"
              className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white md:col-span-2" data-testid="reminder-title" />
            <input type="date" value={rem.date} onChange={(e) => setRem({ ...rem, date: e.target.value })}
              className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white" data-testid="reminder-date" />
            <input type="time" value={rem.time} onChange={(e) => setRem({ ...rem, time: e.target.value })}
              className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white" data-testid="reminder-time" />
            <textarea value={rem.description} onChange={(e) => setRem({ ...rem, description: e.target.value })} placeholder="Notes (optionnel)"
              className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white md:col-span-2" rows={2} data-testid="reminder-desc" />
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={createReminder} disabled={savingRem} className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-2" data-testid="reminder-save-btn">
              {savingRem ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Créer le rappel
            </button>
            <button onClick={() => setShowReminder(false)} className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-300 text-sm">Annuler</button>
          </div>
        </div>
      )}

      {/* Connexion Google Calendar */}
      {status && !status.calendar_access && (
        <div className="bg-amber-950/30 border border-amber-800/50 rounded-xl p-4 mb-5" data-testid="connect-calendar-banner">
          <div className="flex items-start gap-3">
            <CircleAlert className="text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-amber-200">Connecte ton Google Agenda</div>
              <p className="text-amber-100/80 text-sm mt-1">
                {status.connected
                  ? `Compte Google connecté (${status.google_email}), mais l'accès Agenda n'est pas encore autorisé. Reconnecte-toi pour l'activer.`
                  : "Aucun compte Google connecté."}
              </p>
              <p className="text-amber-100/60 text-xs mt-2">
                Note : l'API "Google Calendar" doit aussi être activée dans Google Cloud Console (projet Gmail existant).
              </p>
              <button onClick={connectCalendar} className="mt-3 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm flex items-center gap-2" data-testid="connect-calendar-btn">
                <LinkIcon size={16} /> {status.connected ? "Reconnecter Google (autoriser Agenda)" : "Connecter Google Agenda"}
              </button>
            </div>
          </div>
        </div>
      )}

      {status?.calendar_access && (
        <div className="bg-emerald-950/30 border border-emerald-800/40 rounded-lg px-4 py-2.5 mb-5 flex items-center gap-2 text-sm text-emerald-200">
          <CheckCircle2 size={16} /> Google Agenda connecté ({status.google_email})
        </div>
      )}

      {/* Conflits */}
      {data?.conflict_count > 0 && (
        <div className="bg-red-950/40 border border-red-800/60 rounded-xl p-4 mb-5" data-testid="conflicts-panel">
          <div className="flex items-center gap-2 text-red-300 font-semibold mb-2">
            <AlertTriangle size={18} /> {data.conflict_count} conflit{data.conflict_count > 1 ? "s" : ""} détecté{data.conflict_count > 1 ? "s" : ""}
          </div>
          <div className="space-y-2">
            {data.conflicts.map((c, i) => (
              <div key={i} className="text-sm text-red-100 bg-red-900/30 rounded-lg px-3 py-2">
                <span className="font-medium">{c.a.title}</span> ⟷ <span className="font-medium">{c.b.title}</span>
                <div className="text-xs text-red-200/70">{fmtDate(c.a.start)} · {fmtTime(c.a.start)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data?.google_error && (
        <div className="bg-zinc-800/60 border border-zinc-700 rounded-lg px-4 py-2.5 mb-5 text-sm text-zinc-300">
          ⚠️ {data.google_error}
        </div>
      )}

      {/* Timeline */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-amber-400" /></div>
      ) : dateKeys.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">
          <CalendarDays size={44} className="mx-auto mb-3 opacity-40" />
          <p>Aucun événement sur la période. {!status?.calendar_access && "Connecte ton Google Agenda pour tout voir ici."}</p>
        </div>
      ) : (
        <div className="space-y-5">
          {dateKeys.map((dk) => (
            <div key={dk}>
              <div className="text-amber-300 text-sm font-semibold uppercase tracking-wide mb-2">{fmtDate(dk)}</div>
              <div className="space-y-2">
                {grouped[dk].sort((a, b) => (a.start || "").localeCompare(b.start || "")).map((ev) => (
                  <div key={`${ev.source}-${ev.id}`}
                    className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${ev.in_conflict ? "bg-red-950/30 border-red-800/60" : "bg-zinc-900 border-zinc-800"}`}
                    data-testid={`agenda-event-${ev.id}`}>
                    <div className="text-center w-16 shrink-0">
                      <div className="text-white text-sm font-semibold">{fmtTime(ev.start, ev.all_day)}</div>
                      {!ev.all_day && ev.end && <div className="text-zinc-500 text-[10px]">{fmtTime(ev.end)}</div>}
                    </div>
                    <div className={`w-1 self-stretch rounded ${ev.source === "google" ? "bg-sky-500" : "bg-amber-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-medium truncate flex items-center gap-2">
                        {ev.in_conflict && <AlertTriangle size={14} className="text-red-400 shrink-0" />}
                        {ev.title}
                      </div>
                      <div className="text-zinc-500 text-xs flex items-center gap-2 mt-0.5">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${ev.source === "google" ? "bg-sky-500/20 text-sky-300" : "bg-amber-500/20 text-amber-300"}`}>
                          {ev.source === "google" ? "Google" : "KAIZEN"}
                        </span>
                        {ev.location && <span className="truncate">{ev.location}</span>}
                      </div>
                    </div>
                    {ev.source === "google" && ev.html_link && (
                      <a href={ev.html_link} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-white shrink-0"><ExternalLink size={15} /></a>
                    )}
                    {ev.source === "kaizen" && status?.calendar_access && (
                      <button onClick={() => pushToGoogle(ev)} className="text-zinc-400 hover:text-amber-300 shrink-0" title="Ajouter à Google Agenda"><Upload size={15} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
